/**
 * The guardrail that warns and never blocks (3.2). When a move disagrees with
 * the ordered frequency, the 30-day period or what the patient told us at the
 * welcome call, this says so in the patient's language and then honours the
 * decision. Nothing is written when the clinician proceeds: the consequence
 * shows up as the patient's own row on the worklist, which never names who
 * moved what.
 */
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import type { RealMoveEvaluation } from "../../engine/moveGuard";
import type { TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";
import { useDemoBranchClock } from "../../components/branchClock";

export function MoveAnywayDialog({
  patientName,
  toLabel,
  evaluation,
  reasons,
  onKeep,
  onMoveAnyway,
}: {
  patientName: string;
  toLabel: string;
  evaluation: RealMoveEvaluation;
  /**
   * The reasons to list in place of the guard's own sentences. My week passes the
   * closed-list text its day marks use (`dialogReasons`), so the dialog reads as
   * the day's popper did, and leaves out the checks line: its period-risk figure
   * restates the week's shortfall the reasons already name. My day passes
   * nothing and reads as it always has.
   */
  reasons?: TimeSentence[];
  onKeep: () => void;
  onMoveAnyway: () => void;
}) {
  const clock = useDemoBranchClock();
  return (
    <Dialog open onClose={onKeep} maxWidth="sm" fullWidth>
      <DialogTitle>
        Move {patientName} to {toLabel}?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Alert severity="warning" variant="outlined">
            <Stack spacing={0.5}>
              {(reasons ?? evaluation.reasons).map((reason) => clock.sentence(reason)).map((reason) => (
                <Typography key={reason} variant="body2">
                  {reason}
                </Typography>
              ))}
            </Stack>
          </Alert>
          {!reasons && (
            <Typography variant="body2" color="text.secondary">
              {[
                evaluation.checks.frequency && `Frequency this week ${evaluation.checks.frequency}`,
                evaluation.checks.periodRisk && `Visits at risk ${evaluation.checks.periodRisk}`,
                evaluation.checks.window && `Patient window ${clock.sentence(evaluation.checks.window)}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onKeep}>Keep</Button>
        <Button variant="contained" color="warning" onClick={onMoveAnyway}>
          Move anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
